import { getPayPledge, pledgeKey, resolvePledgeRow, setSelectedPayPledges } from '../../pledge-pay/pay-pledge-map';
import { isTruthy } from '../../shared/is-truthy';
import { readPledgeActionDataFromRow } from '../row-expander/row-data';
import { showAlertMsg } from '../show-alert-msg';
function requireHandler(fn, name) {
    if (typeof fn !== 'function') {
        throw new Error(name + ' is required');
    }
    return fn;
}

export default function createPledgesRowActionHandlers(deps) {
    const {
        ResendReceipt,
        ViewSentStatements,
        openEditModal,
        EditOngoingModal,
        CancelRecurringConfirm,
        OpenAssociateModal,
        openInvoiceModal,
        openDeleteModal,
        OpenViewRecurPayments,
        EditPaidDonationPledge,
        openDonationToPledgeModal,
        openPrintStatementModal,
        openPayBillModal,
        openPayInstallmentsModal,
        openMemberPayBillPage,
        viewAllMemberPledge: viewAllMemberPledgeFn,
        activateRecurringSchedule,
    } = deps;

    if (typeof openPayBillModal !== 'function' && typeof openMemberPayBillPage !== 'function') {
        throw new Error('createPledgesRowActionHandlers: openPayBillModal or openMemberPayBillPage is required');
    }

    function resolvePledgeForPayment($btn) {
        const pledgeId = String($btn.attr('data-pledge-id') || '').trim();
        const tableName = String($btn.attr('data-table-name') || '').trim();
        if (!pledgeId) {
            throw new Error('open-pay-bill-modal: data-pledge-id is required');
        }
        if (!tableName) {
            throw new Error('open-pay-bill-modal: data-table-name is required');
        }
        const $row = $btn.closest('.clickable-row');
        const rowData = $row.length ? readPledgeActionDataFromRow($row) : null;
        const mapPledge = resolvePledgeRow(tableName, pledgeId);
        if (mapPledge && rowData) {
            return { ...rowData, ...mapPledge };
        }
        if (mapPledge) {
            return mapPledge;
        }
        if (rowData) {
            return rowData;
        }
        return null;
    }

    function handlePayBill($btn) {
        if (typeof openMemberPayBillPage === 'function') {
            const pledgeId = String($btn.attr('data-pledge-id') || '').trim();
            if (!pledgeId) {
                throw new Error('open-pay-bill-modal: data-pledge-id is required');
            }
            openMemberPayBillPage(pledgeId);
            return;
        }
        const pledge = resolvePledgeForPayment($btn);
        if (!pledge) {
            showAlertMsg('Could not load pledge data for payment.', 'danger');
            return;
        }
        setSelectedPayPledges([pledge]);
        openPayBillModal(
            pledgeKey(pledge.table_name, pledge.id)
        );
    }
    function handleEditRecurring($btn) {
        requireHandler(EditOngoingModal, 'EditOngoingModal')(
            $btn.attr('data-pledge-id'),
            $btn.attr('data-pledge-type')
        );
    }

    function handleCancelRecurring($btn) {
        requireHandler(CancelRecurringConfirm, 'CancelRecurringConfirm')(
            $btn.attr('data-pledge-id'),
            $btn.attr('data-pledge-type')
        );
    }

    return {
        'resend-receipt': ($btn) => {
            requireHandler(ResendReceipt, 'ResendReceipt')(
                $btn.attr('data-pledge-id'),
                $btn.attr('data-table-name'),
                $btn.attr('data-pledge-type'),
                Number($btn.attr('data-recurring') || 0)
            );
        },
        'view-recur-payments': ($btn) => {
            requireHandler(OpenViewRecurPayments, 'OpenViewRecurPayments')(
                $btn.attr('data-pledge-id'),
                $btn.attr('data-table-name')
            );
        },
        'edit-paid-donation': ($btn) => {
            requireHandler(EditPaidDonationPledge, 'EditPaidDonationPledge')(
                $btn.attr('data-pledge-id'),
                $btn.attr('data-table-name'),
                $btn.attr('data-pledge-type')
            );
        },
        'apply-donation-to-pledge': ($btn) => {
            requireHandler(openDonationToPledgeModal, 'openDonationToPledgeModal')({
                donationId: $btn.attr('data-pledge-id'),
                amount: $btn.attr('data-amount'),
                userId: $btn.attr('data-user-id'),
            });
        },
        'view-sent-statements': ($btn) => {
            const tableName = $btn.attr('data-table-name');
            const pledge = getPayPledge(pledgeKey(tableName, $btn.attr('data-pledge-id')));
            if (!pledge) {
                showAlertMsg('Unable to open sent statements.', 'danger');
                return;
            }
            requireHandler(ViewSentStatements, 'ViewSentStatements')(pledge);
        },
        'edit-ongoing': handleEditRecurring,
        'edit-recurring': handleEditRecurring,
        'activate-recurring': ($btn) => {
            requireHandler(activateRecurringSchedule, 'activateRecurringSchedule')($btn);
        },
        'cancel-recurring': handleCancelRecurring,
        'open-edit-modal': ($btn) => {
            requireHandler(openEditModal, 'openEditModal')($btn.attr('data-pledge-id'));
        },
        'open-delete-modal': ($btn) => {
            requireHandler(openDeleteModal, 'openDeleteModal')(
                $btn.attr('data-pledge-id'),
                isTruthy($btn.attr('data-delete-paid'))
            );
        },
        'open-pay-bill-modal': handlePayBill,
        'pay-bill': handlePayBill,
        'open-pay-installments-modal': ($btn) => {
            requireHandler(openPayInstallmentsModal, 'openPayInstallmentsModal')($btn);
        },
        'open-invoice-modal': ($btn) => {
            requireHandler(openInvoiceModal, 'openInvoiceModal')($btn.attr('data-pledge-id'), 'true');
        },
        'open-print-statement-modal': ($btn) => {
            requireHandler(openPrintStatementModal, 'openPrintStatementModal')(
                $btn.attr('data-pledge-id'),
                $btn.attr('data-user-id')
            );
        },
        'open-associate-modal': ($btn) => {
            requireHandler(OpenAssociateModal, 'OpenAssociateModal')(
                $btn.attr('data-pledge-id'),
                $btn.attr('data-table-name'),
                $btn.attr('data-pledge-type'),
                $btn.attr('data-user-name') || '',
                $btn.attr('data-amount') || '',
                $btn.attr('data-description') || '',
                $btn.attr('data-associate-mode') || 'associate'
            );
        },
        'view-all-member-pledges': ($btn) => {
            requireHandler(viewAllMemberPledgeFn || viewAllMemberPledge, 'viewAllMemberPledge')(
                $btn.attr('data-user-id'),
                'pledges'
            );
        },
    };
}
